import { TFunction } from "i18next";
import { useForm } from "react-hook-form";

import { Button, InputText, Text } from "@pentabd/ui";

import { useLanguage } from "@hooks/useLanguage";
import { StatusBadge } from "@components";
import { CustomSelectedProducts } from "../..";

type Input = {
  [key: string]: number;
};

export const AssignModalTableColumn = (
  t: TFunction<"translation", undefined>,
  handleProductSelection: any,
  notAssignedAmount: number,
  selectedProducts: CustomSelectedProducts[] | [],
  productType: any,
  handleAssignAmount: any,
  rowData: any,
) => {
  const { language } = useLanguage();

  const { register, setValue } = useForm<Input>();

  return [
    {
      id: 1,
      key: "assetNameEn",
      name: t(
        "INVENTORY_REQUISITION.REQUISITION_DETAILS.ASSIGN_MODAL_TABLE.COLUMN.NAME",
      ),
      render: (nameEn: string, row: any) => {
        const name = language === "en" ? row?.nameEn : row?.nameBn;
        return <Text color="subtitle1">{name ? name : "..."}</Text>;
      },
    },

    {
      id: 2,
      key: "lotNo",
      name: t(
        "INVENTORY_REQUISITION.REQUISITION_DETAILS.ASSIGN_MODAL_TABLE.COLUMN.LOT_NUMBER",
      ),
      render: (lotNo: number, row: any) => {
        return <Text color="subtitle1">{lotNo ? lotNo : "..."}</Text>;
      },
    },
    {
      id: 3,
      key: "serialNo",
      name:
        productType === "ASSET" &&
        t(
          "INVENTORY_REQUISITION.REQUISITION_DETAILS.ASSIGN_MODAL_TABLE.COLUMN.SERIAL_NUMBER",
        ),
      render: (serialNo: number | string) => {
        if (productType === "ASSET") {
          return <Text color="subtitle1">{serialNo ? serialNo : "..."}</Text>;
        } else {
          return <></>;
        }
      },
    },
    {
      id: 4,
      key: "amount",
      name:
        productType !== "ASSET" &&
        t(
          "INVENTORY_REQUISITION.REQUISITION_DETAILS.ASSIGN_MODAL_TABLE.COLUMN.AVAILABLE_AMOUNT",
        ),
      render: (amount: number | string) => {
        if (productType !== "ASSET") {
          return <Text color="subtitle1">{amount ? amount : 0}</Text>;
        } else {
          return <></>;
        }
      },
    },
    {
      id: 5,
      key: "condition",
      name:
        productType === "ASSET" &&
        t(
          "INVENTORY_REQUISITION.REQUISITION_DETAILS.ASSIGN_MODAL_TABLE.COLUMN.CONDITION",
        ),
      render: (condition: any, row: any) => {
        if (productType === "ASSET") {
          return <StatusBadge status={condition ? condition : "Good"} />;
        } else {
          return null;
        }
      },
    },
    {
      id: 6,
      key: "approve_amount",
      name:
        productType !== "ASSET" &&
        t(
          "INVENTORY_REQUISITION.REQUISITION_DETAILS.ASSIGN_MODAL_TABLE.COLUMN.ASSIGN_AMOUNT",
        ),
      render: (_: any, row: any) => {
        if (productType !== "ASSET") {
          const availableAmount = Number(row?.amount || 0);

          // find the opened modal row
          const openedModalIndex = selectedProducts?.findIndex(
            (item: CustomSelectedProducts) =>
              item.modalId === rowData?.data?.id,
          );

          // find the selected product in the opened modal
          let active = false;
          if (openedModalIndex !== -1) {
            active = selectedProducts[openedModalIndex]?.products.some(
              (item: any) => item.id === row.id,
            );
          } else {
            active = false;
          }

          return (
            <InputText
              {...register(`approve_amount_${row.id}`, {
                required: "Approve amount is required",
                min: 0,
              })}
              onChange={(e) => {
                const targetValue = Number(e.target.value || 0);

                if (targetValue > 0) {
                  const totalItems = selectedProducts[
                    openedModalIndex
                  ]?.products.reduce((prev: number, item: any) => {
                    if (row.id === item?.id) {
                      return prev + targetValue;
                    } else {
                      return prev + (item?.assignAmount || 0);
                    }
                  }, 0);
                  if (
                    totalItems >
                    selectedProducts[openedModalIndex]?.notAssignedAmount
                  ) {
                    const newTargetValue: number =
                      selectedProducts[openedModalIndex]?.notAssignedAmount -
                      (totalItems - targetValue);
                    setValue(
                      `approve_amount_${row.id}`,
                      Number(
                        newTargetValue >= availableAmount
                          ? availableAmount
                          : newTargetValue,
                      ),
                    );
                    handleAssignAmount(
                      row.id,
                      newTargetValue >= availableAmount
                        ? availableAmount
                        : newTargetValue,
                    );
                  } else {
                    setValue(
                      `approve_amount_${row.id}`,
                      targetValue >= availableAmount
                        ? availableAmount
                        : targetValue,
                    );
                    handleAssignAmount(
                      row.id,
                      targetValue >= availableAmount
                        ? availableAmount
                        : targetValue,
                    );
                  }
                } else {
                  setValue(`approve_amount_${row.id}`, 0);
                  handleAssignAmount(row.id, 0);
                }
              }}
              defaultValue="0"
              size="sm"
              placeholder="Enter approve amount"
              type="number"
              controlling
              disabled={!active}
            />
          );
        } else {
          return <></>;
        }
      },
    },
    {
      id: 7,
      key: "select",
      render: (_: any, row: any) => {
        // find the opened modal row
        const openedModalIndex = selectedProducts?.findIndex(
          (item: CustomSelectedProducts) => item.modalId === rowData?.data?.id,
        );

        // find the selected product in the opened modal
        let active = false;
        if (openedModalIndex !== -1) {
          active = selectedProducts[openedModalIndex]?.products.some(
            (item: any) => item.id === row.id,
          );
        } else {
          active = false;
        }

        // find the maximum amount asset that can be assigned
        const allAssetSelected =
          selectedProducts[openedModalIndex]?.products?.length >=
          selectedProducts[openedModalIndex]?.notAssignedAmount;

        //find total assigned Items
        const totalItems = selectedProducts[openedModalIndex]?.products.reduce(
          (prev: number, item: any) => {
            if (productType === "ITEM") {
              return prev + (item?.assignAmount || 0);
            } else {
              return prev;
            }
          },
          0,
        );

        //all selected items
        const totalItemSelected =
          totalItems >= selectedProducts[openedModalIndex]?.notAssignedAmount
            ? true
            : false;

        // all selected products
        const allSelected =
          productType === "ASSET" ? allAssetSelected : totalItemSelected;

        return (
          <Button
            fill="fill"
            type="primary"
            size="xs"
            disabled={active || allSelected}
            onClick={async (e) => {
              handleProductSelection(row);
            }}
          >
            {t("INVENTORY_REQUISITION.REQUISITION_DETAILS.SELECT")}
          </Button>
        );
      },
    },
  ];
};
